import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export interface OfflineChunk {
    key: string; // "bookId/filename"
    bookId: string;
    filename: string;
    size: number;
    timestamp: number;
    pinned: boolean;
    smart: boolean;
}

interface OfflineDB extends DBSchema {
    chunks: {
        key: string;
        value: OfflineChunk;
        indexes: { 'by-book': string, 'by-time': number };
    };
}

const DB_NAME = 'corpora-offline-store';
const STORE_NAME = 'chunks';

class OfflineStoreService {
    private dbPromise: Promise<IDBPDatabase<OfflineDB>>;
    private root: FileSystemDirectoryHandle | null = null;
    private opfsSupported = false;

    constructor() {
        this.dbPromise = openDB<OfflineDB>(DB_NAME, 1, {
            upgrade(db) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                store.createIndex('by-book', 'bookId');
                store.createIndex('by-time', 'timestamp');
            },
        });

        if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.getDirectory) {
            this.opfsSupported = true;
            navigator.storage.getDirectory()
                .then(r => { this.root = r; })
                .catch(e => {
                    console.error("OPFS Access Failed", e);
                    this.opfsSupported = false;
                });
        }
    }

    private getKey(bookId: string, filename: string) {
        return `${bookId}/${filename}`;
    }

    private async getRoot(): Promise<FileSystemDirectoryHandle | null> {
        if (!this.opfsSupported) return null;
        if (this.root) return this.root;
        try {
            this.root = await navigator.storage.getDirectory();
            return this.root;
        } catch (e) {
            console.error("Failed to acquire OPFS root directory", e);
            return null;
        }
    }

    async saveChunk(bookId: string, filename: string, blob: Blob, type: 'smart' | 'pinned') {
        const root = await this.getRoot();
        if (!root) {
            console.warn("OPFS is not supported or accessible. Chunk not cached offline.");
            return;
        }

        try {
            const dir = await root.getDirectoryHandle(bookId, { create: true });
            const fileHandle = await dir.getFileHandle(filename, { create: true });
            
            // @ts-ignore
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();

            const db = await this.dbPromise;
            const key = this.getKey(bookId, filename);
            const existing = await db.get(STORE_NAME, key);

            const isPinned = type === 'pinned' || (existing?.pinned || false);
            const isSmart = type === 'smart' || (existing?.smart || false);

            await db.put(STORE_NAME, {
                key,
                bookId,
                filename,
                size: blob.size,
                timestamp: Date.now(),
                pinned: isPinned,
                smart: isSmart
            });
        } catch (e) {
            console.error(`Failed to save chunk ${filename} offline:`, e);
            throw e;
        }
    }

    async getChunk(bookId: string, filename: string): Promise<Blob | null> {
        const root = await this.getRoot();
        if (!root) return null;

        try {
            const dir = await root.getDirectoryHandle(bookId);
            const fileHandle = await dir.getFileHandle(filename);
            const file = await fileHandle.getFile();
            
            // Update LRU time in background if it's smart cached
            const db = await this.dbPromise;
            const key = this.getKey(bookId, filename);
            const meta = await db.get(STORE_NAME, key);
            if (meta) {
                meta.timestamp = Date.now();
                await db.put(STORE_NAME, meta);
            }

            return file;
        } catch (e) {
            // Not found is normal when offline sync is incomplete
            return null;
        }
    }

    async deleteBookOffline(bookId: string) {
        const db = await this.dbPromise;
        const root = await this.getRoot();
        
        try {
            if (root) {
                await root.removeEntry(bookId, { recursive: true });
            }
        } catch (e) {
            console.warn(`Failed to delete OPFS folder for book ${bookId}:`, e);
        }

        try {
            const chunks = await db.getAllFromIndex(STORE_NAME, 'by-book', bookId);
            const tx = db.transaction(STORE_NAME, 'readwrite');
            for (const chunk of chunks) {
                await tx.store.delete(chunk.key);
            }
            await tx.done;
        } catch (e) {
            console.error(`Failed to clear DB metadata for book ${bookId}:`, e);
        }
    }

    async hasBookOffline(bookId: string): Promise<boolean> {
        const db = await this.dbPromise;
        try {
            const chunks = await db.getAllFromIndex(STORE_NAME, 'by-book', bookId);
            const hasPlaylist = chunks.some(c => c.filename === 'playlist.json');
            const hasAudio = chunks.some(c => c.filename !== 'playlist.json');
            return hasPlaylist && hasAudio;
        } catch {
            return false;
        }
    }
    
    async getBookOfflineProgress(bookId: string): Promise<{ downloaded: number; total: number }> {
        const db = await this.dbPromise;
        try {
            const chunks = await db.getAllFromIndex(STORE_NAME, 'by-book', bookId);
            const playlistChunk = chunks.find(c => c.filename === 'playlist.json');
            if (!playlistChunk) return { downloaded: 0, total: 0 };
            
            const file = await this.getChunk(bookId, 'playlist.json');
            if (!file) return { downloaded: 0, total: 0 };
            
            const text = await file.text();
            const playlistData = JSON.parse(text);
            const tracks = playlistData.tracks || [];
            
            const audioChunks = chunks.filter(c => c.filename !== 'playlist.json');
            return {
                downloaded: audioChunks.length,
                total: tracks.length
            };
        } catch {
            return { downloaded: 0, total: 0 };
        }
    }
}

export const offlineStore = new OfflineStoreService();
